/*
 * react-router v6 shim.
 *
 * v3 (the version this app was built on) auto-injects `params`, `location`,
 * `router`, and `children` as props on any component rendered by a <Route>.
 * v6 has no such auto-injection -- everything is hook-based (useParams,
 * useLocation, useNavigate), and hooks don't work inside class components.
 *
 * Rather than rewriting every page/form class component (and their tests,
 * which already render these classes directly with manually-mocked
 * `params`/`router` props) to hooks, this wraps each route's component with
 * an adapter that reads the hooks and re-injects the same v3-shaped props
 * the components already expect. <Outlet/> is passed down as `children` so
 * layout components that do `{this.props.children}` keep working unchanged.
 */
import React from "react";
import { useParams, useLocation, useNavigate, Outlet } from "react-router-dom";
import { createHashHistory } from "history";

// Shared history instance so code outside the React tree (e.g. the global
// $.ajaxError handler in index.jsx) can still navigate imperatively, the
// same way `hashHistory.push(...)` did in v3. Passed into <HistoryRouter>
// in Routes.jsx so React Router drives off this same instance.
export const history = createHashHistory();

export const withRouterProps = (Component) => {
	const WithRouterProps = (props) => {
		const params = useParams();
		const location = useLocation();
		const navigate = useNavigate();

		const router = {
			push: (path) => navigate(path),
			replace: (path) => navigate(path, { replace: true }),
			goBack: () => navigate(-1),
			isActive: (path) => location.pathname === path,
		};

		return (
			<Component {...props} params={params} location={location} router={router}>
				<Outlet/>
			</Component>
		);
	};
	WithRouterProps.displayName = `withRouterProps(${Component.displayName || Component.name || "Component"})`;
	return WithRouterProps;
};
